export interface EInvoiceSchemaLabel {
  term: string;
  label: string;
  title: string;
  description?: string;
}

export interface EInvoiceSchemaLabelIndex {
  byTerm: Map<string, EInvoiceSchemaLabel>;
  byModelPath: Map<string, EInvoiceSchemaLabel>;
}

export const EMPTY_SCHEMA_LABEL_INDEX: EInvoiceSchemaLabelIndex = {
  byTerm: new Map<string, EInvoiceSchemaLabel>(),
  byModelPath: new Map<string, EInvoiceSchemaLabel>()
};

export function buildSchemaLabelIndex(schema: unknown): EInvoiceSchemaLabelIndex {
  const byTerm = new Map<string, EInvoiceSchemaLabel>();
  const byModelPath = new Map<string, EInvoiceSchemaLabel>();
  const root = asRecord(schema) ?? {};
  const definitions = asRecord(root['definitions']) ?? {};

  const register = (node: Record<string, unknown>, path: string[]): void => {
    const schemaLabel = parseSchemaTitle(node['title'], node['description']);
    if (!schemaLabel) {
      return;
    }
    if (!byTerm.has(schemaLabel.term)) {
      byTerm.set(schemaLabel.term, schemaLabel);
    }
    const modelPath = path.length > 0 ? `/${path.join('/')}` : undefined;
    if (modelPath && !byModelPath.has(modelPath)) {
      byModelPath.set(modelPath, schemaLabel);
    }
  };

  const descend = (node: Record<string, unknown> | undefined, path: string[], seenRefs: ReadonlySet<string>): void => {
    if (!node) {
      return;
    }

    const ref = typeof node['$ref'] === 'string' ? node['$ref'] : undefined;
    if (ref) {
      if (seenRefs.has(ref)) {
        return;
      }
      seenRefs = new Set(seenRefs).add(ref);
    }

    const resolved = resolveSchemaRef(node, definitions);
    if (resolved['type'] === 'array' || resolved['items'] !== undefined) {
      descend(asRecord(resolved['items']), path, seenRefs);
      return;
    }

    const properties = asRecord(resolved['properties']);
    if (!properties) {
      return;
    }

    for (const [key, child] of Object.entries(properties)) {
      const childNode = asRecord(child);
      if (!childNode) {
        continue;
      }
      const childPath = [...path, key.trim()];
      register(childNode, childPath);
      descend(childNode, childPath, seenRefs);
    }
  };

  descend(root, [], new Set<string>());
  return { byTerm, byModelPath };
}

export function normalizeModelPath(modelPath: string): string {
  const segments = modelPath.split('/').filter(part => part.length > 0 && !isArrayIndex(part));
  return `/${segments.join('/')}`;
}

function resolveSchemaRef(node: Record<string, unknown>, definitions: Record<string, unknown>): Record<string, unknown> {
  const ref = node['$ref'];
  if (typeof ref === 'string' && ref.startsWith('#/definitions/')) {
    const definition = asRecord(definitions[ref.slice('#/definitions/'.length)]) ?? {};
    return { ...definition, ...node };
  }

  return node;
}

function parseSchemaTitle(title: unknown, description?: unknown): EInvoiceSchemaLabel | undefined {
  if (typeof title !== 'string') {
    return undefined;
  }

  const match = /^\s*\[(B[GT]-\d+)\]\s*(.+?)\s*$/.exec(title);
  if (!match) {
    return undefined;
  }

  const trimmedDescription = typeof description === 'string' ? description.trim() : '';
  return {
    term: match[1],
    label: normalizeSchemaLabel(match[2]),
    title,
    description: trimmedDescription.length > 0 ? trimmedDescription : undefined
  };
}

function normalizeSchemaLabel(label: string): string {
  const normalized = label
    .replace(/\s+/g, ' ')
    .replace(/\.$/, '')
    .replace(/^A group of business terms providing information (about|on) /i, '')
    .replace(/^the /i, '')
    .trim();

  return normalized.length > 0
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : label;
}

function isArrayIndex(value: string): boolean {
  return /^\d+$/.test(value);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}